/* eslint-disable import/no-extraneous-dependencies */
/* eslint-disable no-nested-ternary */
import React, { useState, useRef, useCallback, useEffect } from "react";
import MDBox from "components/Basics/MDBox";
import { Divider, Paper, Typography, Skeleton } from "@mui/material";
import "./style.css";
import { socket } from "redux-react/middleware/ws";
import Editor from "@monaco-editor/react"; // Add this import

const TemplateTab = () => {
	const [jsonPayload, setJsonPayload] = useState(
		'{\n  "name": "John Doe",\n  "age": 30,\n  "city": "New York"\n}'
	);
	const [htmlTemplate, setHtmlTemplate] = useState(`<div class="card">
  <h1>Hello {{name}}!</h1>
  <p>You are {{age}} years old.</p>
  <p>You live in {{city}}.</p>
</div>

<style>
  .card {
    border: 1px solid #ddd;
    border-radius: 8px;
    padding: 20px;
    margin: 10px;
    background-color: #f9f9f9;
    font-family: Arial, sans-serif;
  }
  .card h1 {
    color: #333;
    margin-top: 0;
  }
  .card p {
    color: #666;
    line-height: 1.5;
  }
</style>`);
	const [renderedHtml, setRenderedHtml] = useState("");
	const [error, setError] = useState("");
	const [isResizing, setIsResizing] = useState(false); // Add state for resizing

	const resizerRef = useRef(null);
	const leftPanelRef = useRef(null);
	const rightPanelRef = useRef(null);
	const containerRef = useRef(null);

	// Load from localStorage on mount
	useEffect(() => {
		const savedJson = localStorage.getItem("template_jsonPayload");
		const savedHtml = localStorage.getItem("template_htmlTemplate");
		if (savedJson) setJsonPayload(savedJson);
		if (savedHtml) setHtmlTemplate(savedHtml);
	}, []);

	// Save jsonPayload to localStorage on change
	useEffect(() => {
		localStorage.setItem("template_jsonPayload", jsonPayload);
	}, [jsonPayload]);

	// Save htmlTemplate to localStorage on change
	useEffect(() => {
		localStorage.setItem("template_htmlTemplate", htmlTemplate);
	}, [htmlTemplate]);

	// Function to render the template
	const renderTemplate = useCallback(() => {
		try {
			setError("");
			const parsedPayload = JSON.parse(jsonPayload);
			socket.emit(
				"render_template",
				{
					template: htmlTemplate,
					inputs: parsedPayload
				},
				template => {
					setRenderedHtml(template);
				}
			);
		} catch (err) {
			setError(`Error: ${err.message}`);
			setRenderedHtml("");
		}
	}, [jsonPayload, htmlTemplate]);

	// Auto-render when inputs change
	React.useEffect(() => {
		renderTemplate();
	}, [renderTemplate]);

	// Handle resizing
	const handleMouseDown = useCallback(e => {
		e.preventDefault();
		setIsResizing(true); // Set resizing to true

		const startX = e.clientX;
		const containerRect = containerRef.current.getBoundingClientRect();
		const leftPanel = leftPanelRef.current;
		const rightPanel = rightPanelRef.current;
		const initialLeftWidth = leftPanel.offsetWidth;

		const handleMouseMove = moveEvent => {
			const deltaX = moveEvent.clientX - startX;
			const newLeftWidth = initialLeftWidth + deltaX;
			const containerWidth = containerRect.width;
			const minWidth = 200;
			const maxWidth = containerWidth - minWidth - 5; // 5px for the resizer

			if (newLeftWidth >= minWidth && newLeftWidth <= maxWidth) {
				const leftPercent = (newLeftWidth / containerWidth) * 100;
				const rightPercent = 100 - leftPercent - 0.5; // 0.5% for the resizer

				leftPanel.style.width = `${leftPercent}%`;
				rightPanel.style.width = `${rightPercent}%`;
			}
		};

		const handleMouseUp = () => {
			setIsResizing(false); // Set resizing to false
			document.removeEventListener("mousemove", handleMouseMove);
			document.removeEventListener("mouseup", handleMouseUp);
		};

		document.addEventListener("mousemove", handleMouseMove);
		document.addEventListener("mouseup", handleMouseUp);
	}, []);

	return (
		<MDBox mt={2} className="template-container" ref={containerRef}>
			<div className="template-layout">
				{/* Left Panel */}
				<div className="template-left-panel" ref={leftPanelRef}>
					{/* Top Section - JSON Payload Input */}
					<div className="template-json-section">
						<Typography variant="h6" gutterBottom sx={{ fontSize: "0.9rem", fontWeight: "bold" }}>
							Handlebars Payload (JSON)
						</Typography>
						<Editor
							height="200px"
							language="json"
							value={jsonPayload}
							onChange={setJsonPayload}
							options={{
								minimap: { enabled: false },
								fontSize: 14,
								lineNumbers: "on",
								roundedSelection: false,
								scrollBeyondLastLine: false,
								automaticLayout: true
							}}
						/>
					</div>

					<Divider sx={{ my: 1 }} />

					{/* Bottom Section - HTML Template */}
					<div className="template-html-section">
						<Typography variant="h6" gutterBottom sx={{ fontSize: "0.9rem", fontWeight: "bold" }}>
							HTML Template
						</Typography>
						<Editor
							height="100%"
							language="html"
							value={htmlTemplate}
							onChange={setHtmlTemplate}
							options={{
								minimap: { enabled: false },
								fontSize: 14,
								lineNumbers: "on",
								roundedSelection: false,
								scrollBeyondLastLine: false,
								automaticLayout: true
							}}
						/>
					</div>

					{error && (
						<Paper className="template-error">
							<Typography color="error" variant="body2">
								{error}
							</Typography>
						</Paper>
					)}
				</div>

				{/* Resizer */}
				<div
					className="template-resizer"
					ref={resizerRef}
					onMouseDown={handleMouseDown}
					role="button"
					tabIndex={0}
					aria-label="Resize panels"
				/>

				{/* Right Panel - Rendered HTML */}
				<div className="template-right-panel" ref={rightPanelRef}>
					<div className="template-preview">
						{error ? (
							<div className="template-error-display">
								<Typography color="error">{error}</Typography>
							</div>
						) : isResizing ? (
							<Skeleton variant="rectangular" height="100%" />
						) : (
							<iframe
								className="template-rendered-content"
								srcDoc={renderedHtml}
								style={{ width: "100%", height: "100%", border: "none" }}
								title="Rendered Template"
								sandbox="allow-same-origin"
							/>
						)}
					</div>
				</div>
			</div>
		</MDBox>
	);
};

export default TemplateTab;
